import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../clients/apiClient';
import Toast from './Toast';
import type { User } from '../../data/mock';

type ToneKey = 'confident_professional' | 'friendly_conversational' | 'concise_direct' | 'story_driven';

const tones: { key: ToneKey; label: string; preview: string }[] = [
  { key: 'confident_professional', label: 'Confident & Professional', preview: '"I recently led a $2B platform ensuring 99.9% uptime across institutional clients."' },
  { key: 'friendly_conversational', label: 'Friendly & Conversational', preview: '"Hey Sarah, I wanted to reach out directly — I think there\'s a real fit here."' },
  { key: 'concise_direct', label: 'Concise & Direct', preview: '"Applied for PM. One fact: I saved $40K/quarter and shipped on time. Worth a call?"' },
  { key: 'story_driven', label: 'Story-driven', preview: '"Three years ago I built something that changed how our team shipped."' },
];

const toneLabel: Record<ToneKey, string> = {
  confident_professional: 'Confident & Professional',
  friendly_conversational: 'Friendly & Conversational',
  concise_direct: 'Concise & Direct',
  story_driven: 'Story-driven',
};

interface EmailPreview {
  subject: string;
  body: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  defaultRole?: string;
  defaultCompany?: string;
  userName?: string;
}

export default function ProfileSlideOver({ open, onClose, onSaved, defaultRole = 'Software Engineer', defaultCompany = 'Google', userName }: Props) {
  const [summary, setSummary] = useState('');
  const [tone, setTone] = useState<ToneKey>('confident_professional');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Preview state
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mobileTab, setMobileTab] = useState<'profile' | 'preview'>('profile');

  const role = defaultRole || 'Software Engineer';
  const company = defaultCompany || 'Google';

  // Fetch latest profile from DB whenever panel opens
  useEffect(() => {
    if (!open) return;
    setEditing(false);
    setPreview(null);
    setLoading(true);
    apiClient.get<User>('/api/auth/me')
      .then(({ data }) => {
        const s = data.resume_summary ?? '';
        const t = (data.email_tone as ToneKey) ?? 'confident_professional';
        setSummary(s);
        setTone(t);
        setName(data.full_name ?? data.name ?? '');
        // Auto-generate preview if profile already exists
        if (s.trim()) {
          generatePreview(s, t);
        }
      })
      .catch(() => setToast({ message: 'Failed to load profile', type: 'error' }))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const generatePreview = useCallback(async (s: string, t: ToneKey) => {
    if (!s.trim()) { setPreview(null); return; }
    setPreviewLoading(true);
    try {
      const { data } = await apiClient.post<EmailPreview>('/api/preview-email', {
        resume_summary: s,
        email_tone: t,
        role,
        company,
        recruiter_name: 'Sarah',
      });
      setPreview(data);
    } catch {
      // silent — preview is best-effort
    } finally {
      setPreviewLoading(false);
    }
  }, [role, company]);

  const handleToneChange = (t: ToneKey) => {
    setTone(t);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.patch('/api/user/profile', { resume_summary: summary, email_tone: tone, full_name: name.trim() || undefined });
      setToast({ message: 'Profile saved', type: 'success' });
      setEditing(false);
      onSaved?.();
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast({ message: 'Failed to save profile', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasProfile = summary.trim().length > 0;
  const isEditMode = editing || !hasProfile;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Panel — wide two-column layout */}
      <div
        className={`fixed top-0 right-0 h-full w-full lg:w-[860px] bg-card z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>Your Profile for Outreach</h2>
          <button onClick={onClose} className="text-hint hover:text-ink transition-colors" aria-label="Close">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile Tabs Switcher */}
        <div className="lg:hidden flex border-b border-border bg-card px-4 py-2 gap-2">
          <button
            type="button"
            onClick={() => setMobileTab('profile')}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              mobileTab === 'profile' ? 'bg-ink text-white shadow-sm' : 'text-muted hover:text-ink bg-bg'
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Profile
          </button>
          <button
            type="button"
            onClick={() => setMobileTab('preview')}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              mobileTab === 'preview' ? 'bg-ink text-white shadow-sm' : 'text-muted hover:text-ink bg-bg'
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Live Preview
          </button>
        </div>

        {/* Two-column body */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row min-h-0">
          {loading ? (
            <div className="flex-1 p-6 space-y-4 animate-pulse">
              <div className="h-3 bg-border rounded w-1/3" />
              <div className="h-32 bg-bg rounded-card" />
              <div className="h-3 bg-border rounded w-1/4 mt-6" />
              <div className="grid grid-cols-2 gap-2.5">
                {[1,2,3,4].map(i => <div key={i} className="h-20 bg-bg rounded-card" />)}
              </div>
            </div>
          ) : (
            <>
              {/* LEFT — form */}
              <div
                className={`w-full flex-1 min-h-0 lg:w-[400px] lg:flex-initial lg:flex-shrink-0 overflow-y-auto overscroll-contain px-5 sm:px-6 py-5 space-y-6 border-r border-border ${
                  mobileTab === 'profile' ? 'block' : 'hidden lg:block'
                }`}
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {/* Name */}
                <div>
                  <p className="text-sm font-semibold text-ink mb-1">Your Name</p>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Full name"
                      className="w-full border border-border rounded-card px-3.5 py-2.5 text-sm text-ink placeholder:text-hint focus:outline-none focus:border-g bg-bg"
                    />
                  ) : (
                    <p className="text-sm text-ink bg-bg border border-border rounded-card px-3.5 py-2.5">{name || '—'}</p>
                  )}
                </div>

                {/* Background */}
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-semibold text-ink">Your Background</p>
                    {hasProfile && !isEditMode && (
                      <button onClick={() => setEditing(true)} className="text-xs text-g hover:underline font-medium">Edit</button>
                    )}
                  </div>
                  <p className="text-xs text-muted mb-3">Write 3–5 bullet points about your most relevant achievements. This personalizes every outreach email.</p>

                  {!isEditMode && hasProfile ? (
                    <div className="bg-bg border border-border rounded-card px-3.5 py-3 text-sm text-ink leading-relaxed whitespace-pre-wrap">
                      {summary}
                    </div>
                  ) : (
                    <textarea
                      value={summary}
                      onChange={e => setSummary(e.target.value)}
                      rows={7}
                      autoFocus={editing}
                      placeholder={`- Led a $2B hedge fund reporting platform\n- Partnered with 5 investment banks on API integrations\n- MS in Computer Science from NYU\n- Hands-on with Python, SQL, React`}
                      className="w-full border border-border rounded-card px-3.5 py-3 text-sm text-ink placeholder:text-hint focus:outline-none focus:border-g resize-none bg-bg leading-relaxed"
                    />
                  )}
                </div>

                {/* Tone */}
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-semibold text-ink">Email Tone</p>
                    {!isEditMode && hasProfile && (
                      <span className="text-xs text-muted font-medium">{toneLabel[tone]}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted mb-3">How should your outreach emails sound?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {tones.map(({ key, label, preview: tPreview }) => {
                      const selected = tone === key;
                      const isInteractive = isEditMode;
                      const Tag = isInteractive ? 'button' : 'div';
                      return (
                        <Tag
                          key={key}
                          {...(isInteractive ? { onClick: () => handleToneChange(key) } : {})}
                          className={`relative text-left p-3 rounded-card border-2 transition-all ${
                            selected
                              ? 'border-g bg-g-light'
                              : isInteractive
                                ? 'border-border bg-bg hover:border-g/50 cursor-pointer'
                                : 'border-border bg-bg opacity-40'
                          }`}
                        >
                          {selected && (
                            <div className="absolute top-2 right-2 h-4 w-4 rounded-full bg-g flex items-center justify-center">
                              <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                          <p className="text-xs font-semibold text-ink mb-1 pr-5">{label}</p>
                          <p className="text-[11px] text-muted leading-relaxed italic">{tPreview}</p>
                        </Tag>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* RIGHT — email preview */}
              <div
                className={`w-full flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-6 py-5 bg-bg ${
                  mobileTab === 'preview' ? 'block' : 'hidden lg:block'
                }`}
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-ink">Email Preview</p>
                  {previewLoading && (
                    <span className="text-xs text-hint animate-pulse">Generating…</span>
                  )}
                </div>
                <p className="text-xs text-muted mb-4">Click "Preview Email" to generate a sample based on your current background and tone.</p>

                {!summary.trim() ? (
                  <div className="flex flex-col items-center justify-center h-48 text-center">
                    <div className="h-10 w-10 rounded-full bg-card border border-border flex items-center justify-center mb-3">
                      <svg className="h-5 w-5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-sm text-muted">Fill in your background then click Preview Email.</p>
                  </div>
                ) : previewLoading && !preview ? (
                  // Skeleton
                  <div className="bg-card rounded-card border border-border p-5 space-y-3 animate-pulse">
                    <div className="h-3 bg-border rounded w-3/4" />
                    <div className="h-3 bg-border rounded w-1/2" />
                    <div className="border-t border-border my-3" />
                    <div className="h-3 bg-border rounded w-full" />
                    <div className="h-3 bg-border rounded w-full" />
                    <div className="h-3 bg-border rounded w-4/5" />
                    <div className="h-3 bg-border rounded w-full mt-2" />
                    <div className="h-3 bg-border rounded w-3/4" />
                  </div>
                ) : preview ? (
                  <div className={`bg-card rounded-card border border-border p-5 transition-opacity duration-300 ${previewLoading ? 'opacity-50' : 'opacity-100'}`}>
                    {/* Email meta */}
                    <div className="space-y-1 mb-4">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-hint w-14 flex-shrink-0">To</span>
                        <span className="text-xs text-ink font-medium">sarah.johnson@{company.toLowerCase().replace(/\s+/g, '')}.com</span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-hint w-14 flex-shrink-0">Subject</span>
                        <span className="text-xs text-ink font-medium">{preview.subject}</span>
                      </div>
                    </div>
                    <div className="border-t border-border pt-4">
                      <p className="text-sm text-ink mb-3">Hi Sarah,</p>
                      <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{preview.body}</p>
                      <div className="mt-4">
                        <p className="text-sm text-ink">Thanks,</p>
                        <p className="text-sm font-medium text-ink">{userName ?? 'You'}</p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="px-6 py-4 border-t border-border flex-shrink-0 flex gap-3 bg-card">
            {isEditMode && editing && (
              <button
                onClick={() => setEditing(false)}
                className="py-2.5 px-4 border border-border rounded-input text-sm font-medium text-muted hover:text-ink hover:bg-bg transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => {
                generatePreview(summary, tone);
                setMobileTab('preview');
              }}
              disabled={previewLoading || !summary.trim()}
              className="py-2.5 px-4 border border-g text-g rounded-input text-sm font-medium hover:bg-g-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {previewLoading ? 'Generating…' : 'Preview Email'}
            </button>
            {isEditMode && (
              <button
                onClick={handleSave}
                disabled={saving || !summary.trim()}
                className="flex-1 py-2.5 bg-ink hover:bg-black text-white rounded-input text-sm font-medium transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save Profile'}
              </button>
            )}
          </div>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}
